import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  CurecallVariableButton,
  CurecallTag,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallSize,
  TrashIcon,
} from '@honestica/curecall-ui'
import {
  EHumanReviewRequestProblem,
  EHumanReviewRequestSubject,
} from 'src/pages/dashboard/_components/human-review-requests.type'
import { useProblemLabels } from 'src/pages/dashboard/_components/use-problem-labels'
import { type TNodeAction } from '../../node.type'
import { ConvocationProblemDropdown } from './convocation-problem-dropdown.component'
import { useChatbotFlow } from '../../chatbot.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'

interface ActionDashboardSectionProps {
  actionPath: string
  onUpdate: (action: Partial<TNodeAction>) => void
}

export function ActionDashboardSection({ actionPath, onUpdate }: ActionDashboardSectionProps) {
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext()
  const { dashboardSubject } = useChatbotFlow()
  const { isAtLeastOneFeatureActivated } = useCurrentProject()
  const problemLabels = useProblemLabels()
  const [showMedAlertError, setShowMedAlertError] = useState(false)

  if (!isAtLeastOneFeatureActivated(['dashboard', 'dashboard-v2'])) {
    return null
  }

  const action = watch(actionPath) as TNodeAction | undefined

  const handleAdd = () => {
    if (!action?.medAlertId) {
      setShowMedAlertError(true)
      return
    }
    setShowMedAlertError(false)
    setValue(`${actionPath}.requestHumanReviewProblem`, EHumanReviewRequestProblem.DECLARED_COMPLICATION, {
      shouldDirty: true,
    })
    onUpdate({ requestHumanReviewProblem: EHumanReviewRequestProblem.DECLARED_COMPLICATION })
  }

  const handleProblemSelect = (problem: EHumanReviewRequestProblem) => {
    setValue(`${actionPath}.requestHumanReviewProblem`, problem, { shouldDirty: true })
    onUpdate({ requestHumanReviewProblem: problem })
  }

  const handleRemove = () => {
    setValue(`${actionPath}.requestHumanReviewProblem`, undefined, { shouldDirty: true })
    onUpdate({ requestHumanReviewProblem: undefined })
  }

  if (action?.requestHumanReviewProblem) {
    return (
      <div className="c-flex c-items-center c-gap-1">
        <CurecallTag status="INFORMATION">
          {action.requestHumanReviewProblem !== EHumanReviewRequestProblem.UNKNOWN
            ? t('pages.configuration.followups.chatbot.conditional-sections.dashboard-tag-with-problem', {
                problem: problemLabels[action.requestHumanReviewProblem],
              })
            : t('pages.configuration.followups.chatbot.conditional-sections.dashboard-tag')}
        </CurecallTag>
        <CurecallButton
          iconType={<TrashIcon />}
          isGhostButton
          onClick={handleRemove}
          appearance={ECurecallButtonAppearance.CANCEL}
          size={ECurecallSize.SMALL}
          ariaLabelText={t('common.actions.delete')}
        />
      </div>
    )
  }

  if (dashboardSubject === EHumanReviewRequestSubject.CONVOCATION) {
    return <ConvocationProblemDropdown onSelect={handleProblemSelect} />
  }

  return (
    <>
      <CurecallVariableButton isAddingButton={true} onClick={handleAdd}>
        {t('pages.configuration.followups.chatbot.conditional-sections.add-dashboard')}
      </CurecallVariableButton>
      {showMedAlertError && (
        <span className="c-basis-full c-text-sm c-text-danger-600">
          {t('pages.configuration.followups.chatbot.conditional-sections.medalert-required-for-dashboard')}
        </span>
      )}
    </>
  )
}
